import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import { notify } from '../../utils';
import { useHistory, useParams } from "react-router-dom";
import { REQUIRED_ERROR, USER_ROLES } from '../../utils/constants';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { UserServices } from '../../Services/UserServices';
import CircularProgress from '@material-ui/core/CircularProgress';
import AppLoader from '../../Components/AppLoader';
import { Paths } from '../../Routes/routePaths';
import { ProjectServices } from '../../Services/ProjectServices';
import { FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@material-ui/core';
import { useAuthDispatch, useAuthState } from '../../Context';
import commonStyles, { pageTitleStyles } from 'theme/commonStyles';
import CreateUserForm from './components/CreateUserForm';
import UserLearningHistory from './components/UserLearningHistory';
import { useEffect } from 'react';
import {
    applyProfileToPayload,
    emptyProfileFields,
    formatApiErrorMessage,
    profileFieldsFromUser,
    validateProfileFields,
} from '../../utils/userProfileFields';
import { useImpersonation } from '../../hooks/useImpersonation';
import { beginImpersonation } from '../../Services/deviareAuth';
import {
    clearInactiveProgrammeFields,
    normalizeProgrammeOptions,
    profileFieldsForProgramme,
} from '../../utils/programmeOptions';

const userServices = new UserServices();
const projectService = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    inputStyle: {
        borderColor: '#838383',
        borderRadius: '25px',
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        margin: '0 0 24px'
    },
    heading: {
        ...pageTitleStyles
    },
    btn: {
        padding: '8px 16px',
        minHeight: 40,
        height: 40,
        fontSize: '13px',
        color: 'white',
        borderRadius: '5px',
        marginLeft: '15px',
    },
    signInBtn: {
        marginRight: 28,
    },
    cancelBtn: {
        padding: '8px 16px',
        minHeight: 40,
        height: 40,
        background: '#B7B7B7',
        fontSize: '13px',
        color: 'white',
        borderRadius: '5px',
        marginLeft: '15px',
        "&:hover": {
            background: "#454343"
        }
    },
    placeBtn: {
        display: 'flex',
        justifyContent: 'end',
        padding: '20px 0',
        marginTop: 30
    },
    pageLoader: {
        minHeight: 320,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnPwd: {
        borderRadius: '25px',
        padding: 16,
        top: 0,
        left: 177,
        position: 'absolute'
    },
}));

const initFormState = {
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    location: '',
    customers: [],
    role: '',
    is_active: false,
    password: '',
    sub_role: '',
    project: null,
    ...emptyProfileFields(),
};

const UpdateUsers = () => {

    const dispatch = useAuthDispatch();
    const { userDetails } = useAuthState(dispatch);
    const history = useHistory();

    const { id } = useParams();
    const classes = useStyles();
    const commonClasses = commonStyles();
    const [companyList, setCompanyList] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [pageLoading, setPageLoading] = React.useState(true);
    const [customers, setCustomers] = React.useState([]);
    const [formState, setFormState] = React.useState({ ...initFormState });
    const [roleList, setRoleList] = React.useState([]);
    const [projectList, setProjectList] = React.useState([]);
    const [programmeOptions, setProgrammeOptions] = React.useState(null);
    const [editUserData, setEditUserData] = React.useState(null);
    const impersonation = useImpersonation();
    const [signInOpen, setSignInOpen] = React.useState(false);
    const [signInReason, setSignInReason] = React.useState('');
    const [signInLoading, setSignInLoading] = React.useState(false);

    React.useEffect(() => {
        projectService.getProgrammeOptions()
            .then(result => {
                if (result?.status) setProgrammeOptions(normalizeProgrammeOptions(result));
            })
            .catch(error => notify('error', error?.response?.data?.message || 'Could not load programme options.'));
        if (id) {
            getProjectList();
        } else {
            setPageLoading(false);
        }
    // Programme/project metadata and the route user are loaded once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getProjectList = async () => {
        try {
            const projectRes = await projectService.getAllLicenseListProject(`project/licenselist`);
            if (projectRes && projectRes.data && projectRes.data.length > 0) {
                const allProject = projectRes.data.map(item => {
                    return {
                        name: item.project_name,
                        id: item.project_uuid,
                        programme_type: item.programme_type || '',
                        programme_type_label: item.programme_type_label || '',
                        programme_subtype: item.programme_subtype || '',
                    }
                });
                setProjectList(allProject);
            }
            await getUserDetails();
        } catch (error) {
            notify("error", "Something Went Wrong");
            setPageLoading(false);
        }
    };

    useEffect(() => {
        if (editUserData) {
            const selectedProject = projectList.find(p => p.id === editUserData.project_id);
            const selectedProj = selectedProject ? {
                ...selectedProject,
                programme_type: selectedProject.programme_type || editUserData.programme_type || '',
                programme_type_label: selectedProject.programme_type_label || editUserData.programme_type_label || '',
            } : null;
            setFormState({
                firstName: editUserData.firstName,
                lastName: editUserData.lastName,
                userName: editUserData.userName,
                email: editUserData.email,
                customers: editUserData.customers,
                location: editUserData.location,
                role: editUserData.role,
                project: selectedProj ? { name: selectedProj.name, id: selectedProj.id } : null,
                is_active: editUserData.is_active,
                ...profileFieldsFromUser(editUserData),
            });
            setPageLoading(false);
        }
    }, [projectList, editUserData]);

    async function getUserDetails() {
        const cusResult = await projectService.getCustomerList();
        if (cusResult && cusResult.data) {
            setCompanyList(cusResult.data);
        }
        let data = await userServices.getUserDetail(id);
        if (data && data.status && data.data && data.data[0]) {
            const userData = data.data[0];
            setEditUserData(userData);
            if (userData?.customers?.length) {
                const selectedComp = cusResult.data ? cusResult.data.filter(cs => userData.customers.includes(cs.uuid)) : [];
                setCustomers(selectedComp);
            }
        } else {
            notify("error", formatApiErrorMessage(data, "Could not load this user"));
            setPageLoading(false);
        }
    }

    React.useEffect(() => {
        const { role } = userDetails;
        let roles = [];
        if (role === USER_ROLES.SUPER_ADMIN) {
            roles = [{
                id: 'customeradmin',
                name: 'Customer Admin'
            },
            {
                id: 'csmadmin',
                name: 'CSM'
            }];
        }
        if (['customeradmin', 'superadmin'].includes(role)) {
            roles.push({
                id: 'projectadmin',
                name: 'Project Admin'
            });
            roles.push({ id: 'gcologist', name: 'GCologist' });
        }
        roles.push({
            id: 'user',
            name: 'User'
        });
        setRoleList(roles);
    }, [userDetails]);

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        setFormState((prev) => (
            name === 'email'
                ? { ...prev, email: value, userName: value }
                : { ...prev, [name]: value }
        ));
    }

    const handleFieldsChange = (patch) => {
        setFormState((prev) => ({ ...prev, ...patch }));
    }

    const handleChange = (event) => {
        setFormState({ ...formState, [event.target.name]: event.target.checked });
    };

    const formValidationCheck = () => {
        if (impersonation.active) {
            notify('error', 'This action is blocked while signed in as a user. Return to the superadmin account first.');
            return null;
        }
        if (!formState.firstName || !formState.lastName || !formState.userName || !formState.email || !formState.location) {
            notify("error", REQUIRED_ERROR);
            return null;
        }
        const programmeType = formState.project?.programme_type || editUserData?.programme_type || '';
        const programmeProfileFields = profileFieldsForProgramme(programmeOptions, programmeType);
        const profileError = validateProfileFields(formState, {
            isEdit: true,
            programmeType,
            profileFields: programmeProfileFields,
        });
        if (profileError) {
            notify("error", profileError);
            return null;
        }
        return handleFormSubmit();
    }

    const handleFormSubmit = async () => {
        setLoading(true);
        let customersIds = customers.map(cs => cs.uuid);
        const programmeType = formState.project?.programme_type || editUserData?.programme_type || '';
        const programmeProfileFields = profileFieldsForProgramme(programmeOptions, programmeType);
        const payload = applyProfileToPayload({
            ...formState,
            userName: formState.email,
            customers: customersIds,
            project_id: (formState.project) ? formState.project.id : null
        }, formState, { isEdit: true, programmeType, profileFields: programmeProfileFields });
        delete payload.project;
        if (impersonation.active) {
            delete payload.password;
        }
        const result = await userServices.addUser(payload, id);
        if (result && result.status) {
            notify("success", result.message)
            setLoading(false);
            history.push(Paths.Users);
        } else {
            notify("error", formatApiErrorMessage(result))
            setLoading(false)
        }
    }

    const targetName = editUserData
        ? [editUserData.firstName, editUserData.lastName].filter(Boolean).join(' ').trim() || editUserData.userName
        : 'this user';
    const canSignInAsUser = !pageLoading
        && !impersonation.active
        && (userDetails?.role === USER_ROLES.SUPER_ADMIN || localStorage.getItem('role') === USER_ROLES.SUPER_ADMIN)
        && editUserData
        && editUserData.role !== USER_ROLES.SUPER_ADMIN
        && String(editUserData.uuid || id) !== String(localStorage.getItem('uuid') || '')
        && editUserData.is_active !== false
        && editUserData.is_active !== 0;

    const handleSignIn = async () => {
        if (signInReason.trim().length > 500) {
            notify('error', 'Reason cannot be longer than 500 characters.');
            return;
        }
        setSignInLoading(true);
        const result = await beginImpersonation(id, signInReason);
        setSignInLoading(false);
        if (!result.ok) {
            notify('error', result.message || 'Could not sign in as this user.');
            return;
        }
        setSignInOpen(false);
        setSignInReason('');
        notify('success', `Signed in as ${result.targetName || targetName}.`);
        history.push(Paths.Dashboard);
    };

    const handleChangeCustomer = (newValue, type) => {
        if (type === 'project') {
            setFormState(clearInactiveProgrammeFields(
                { ...formState, project: newValue },
                newValue?.programme_type || ''
            ));
        } else {
            setCustomers(newValue);
        }
    }

    const generatePassword = evt => {
        evt.preventDefault();
        let newPwd = '';
        const pwdChars =
            'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890*/&%$#@!';
        for (let i = 0; i < 8; i++) {
            newPwd += pwdChars[Math.floor(Math.random() * pwdChars.length)];
        }
        if (newPwd) {
            setFormState({ ...formState, password: newPwd });
        }
    };

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <div className={classes.pageHeader}>
                        <Typography
                            variant="subtitle2"
                            className={classes.heading}
                            color="textPrimary">
                            Update User
                        </Typography>
                        {!pageLoading && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {canSignInAsUser && (
                                    <Button
                                        color="primary"
                                        className={`${classes.btn} ${classes.signInBtn}`}
                                        variant="contained"
                                        onClick={() => setSignInOpen(true)}
                                    >
                                        Sign in as user
                                    </Button>
                                )}
                                <FormControlLabel
                                    control={<Switch checked={formState.is_active} onChange={handleChange} name="is_active" />}
                                    label="Account Status"
                                    disabled={impersonation.active}
                                />
                            </div>
                        )}
                    </div>
                    {pageLoading ? (
                        <AppLoader minHeight={320} />
                    ) : (
                    <CreateUserForm
                        projectList={projectList}
                        formState={formState}
                        handleChangeCustomer={handleChangeCustomer}
                        handleInputChange={handleInputChange}
                        companyList={companyList}
                        roleList={roleList}
                        userDetails={userDetails}
                        customers={customers}
                        emailDisable
                        userNameDisable
                        showProject
                        showPassword={!impersonation.active}
                        adminFieldsLocked={impersonation.active}
                        onGeneratePassword={generatePassword}
                        showProfileFields
                        handleFieldsChange={handleFieldsChange}
                        programmeType={formState.project?.programme_type || editUserData?.programme_type || ''}
                        programmeTypeLabel={formState.project?.programme_type_label || editUserData?.programme_type_label || ''}
                        programmeProfileFields={profileFieldsForProgramme(
                            programmeOptions,
                            formState.project?.programme_type || editUserData?.programme_type || ''
                        )}
                    />
                    )}
                    {!pageLoading && (
                    <div className={classes.placeBtn}>
                        <Button onClick={() => history.push(Paths.Users)} variant="outlined" color="default" className={classes.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            className={classes.btn}
                            variant="contained"
                            disabled={
                                impersonation.active ||
                                !formState.firstName ||
                                !formState.lastName ||
                                !formState.userName ||
                                !formState.email ||
                                !formState.role ||
                                !formState.location
                            }
                            onClick={formValidationCheck}>
                            {loading ? <CircularProgress size={15} color="secondary" /> : 'Save'}
                        </Button>
                    </div>
                    )}
                    {!pageLoading && (
                        <UserLearningHistory details={editUserData?.learning_access_details} />
                    )}
                </div>
            </main>
            <Dialog open={signInOpen} onClose={() => !signInLoading && setSignInOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Sign in as user</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ marginBottom: 16 }}>
                        Open {targetName}&apos;s Deviare API session. Their password is not shown or changed.
                        Keycloak, Moodle, and the other learning sites stay on your superadmin session.
                    </Typography>
                    <TextField
                        label="Reason (optional)"
                        value={signInReason}
                        onChange={(event) => setSignInReason(event.target.value.slice(0, 500))}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        inputProps={{ maxLength: 500 }}
                        helperText={`${signInReason.length}/500`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSignInOpen(false)} disabled={signInLoading}>Cancel</Button>
                    <Button color="primary" variant="contained" onClick={handleSignIn} disabled={signInLoading}>
                        {signInLoading ? <CircularProgress size={15} color="inherit" /> : 'Sign in as user'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
}

export default UpdateUsers;
